export type TranscriptCallback = (text: string, isFinal: boolean) => void
export type TokenProvider = () => Promise<string>

export class LiveTranscriber {
  private ws: WebSocket | null = null
  private stream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private onTranscript: TranscriptCallback
  private getToken: TokenProvider
  private bandId: number | null
  private active = false
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private analyserNode: AnalyserNode | null = null
  private reconnecting = false

  // bandId is forwarded as a query parameter so the backend can pre-check the
  // band's AI credits and deduct after the session ends.
  constructor(onTranscript: TranscriptCallback, getToken: TokenProvider, bandId?: number | null) {
    this.onTranscript = onTranscript
    this.getToken = getToken
    this.bandId = bandId ?? null
  }

  async start() {
    this.active = true

    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })

    this.audioContext = new AudioContext()
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }

    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream)
    this.analyserNode = this.audioContext.createAnalyser()
    this.analyserNode.fftSize = 4096
    this.sourceNode.connect(this.analyserNode)
    const muteGain = this.audioContext.createGain()
    muteGain.gain.value = 0
    this.analyserNode.connect(muteGain)
    muteGain.connect(this.audioContext.destination)

    await this.connectWebSocket()
  }

  private async connectWebSocket() {
    if (!this.active) return

    const token = await this.getToken()
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const bandParam = this.bandId != null ? `&bandId=${this.bandId}` : ''
    const wsUrl = `${protocol}//${window.location.host}/api/transcribe/ws?access_token=${encodeURIComponent(token)}${bandParam}`
    this.ws = new WebSocket(wsUrl)

    this.ws.onopen = () => {
      this.reconnecting = false
      this.startPolling()
    }

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.handleGeminiResponse(data)
      } catch {
        // ignore non-JSON messages
      }
    }

    this.ws.onerror = () => {
      if (this.active) this.reconnect()
    }

    this.ws.onclose = () => {
      if (this.active) this.reconnect()
    }
  }

  private reconnect() {
    if (!this.active || this.reconnecting) return
    this.reconnecting = true

    // Stop polling but keep mic and audio graph alive
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close()
      }
      this.ws = null
    }

    // Reconnect after a short delay
    setTimeout(() => {
      if (this.active) {
        this.connectWebSocket().catch(() => { this.reconnecting = false })
      }
    }, 500)
  }

  private startPolling() {
    if (!this.analyserNode || !this.audioContext) return

    // Clear any existing poll timer
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }

    const nativeSampleRate = this.audioContext.sampleRate
    const targetRate = 16000
    const ratio = nativeSampleRate / targetRate
    const bufferLen = this.analyserNode.fftSize
    const float32 = new Float32Array(bufferLen)

    this.pollTimer = setInterval(() => {
      if (!this.active || !this.ws || this.ws.readyState !== WebSocket.OPEN || !this.analyserNode) return

      this.analyserNode.getFloatTimeDomainData(float32)

      const outputLen = Math.floor(bufferLen / ratio)
      const int16 = new Int16Array(outputLen)
      for (let i = 0; i < outputLen; i++) {
        const srcIdx = i * ratio
        const lo = Math.floor(srcIdx)
        const hi = Math.min(lo + 1, bufferLen - 1)
        const frac = srcIdx - lo
        const sample = float32[lo]! * (1 - frac) + float32[hi]! * frac
        const s = Math.max(-1, Math.min(1, sample))
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
      }

      const bytes = new Uint8Array(int16.buffer)
      let binary = ''
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]!)
      }
      const base64 = btoa(binary)

      this.ws!.send(JSON.stringify({
        realtimeInput: {
          mediaChunks: [{
            mimeType: 'audio/pcm;rate=16000',
            data: base64
          }]
        }
      }))
    }, 100)
  }

  private pendingText = ''
  private flushTimer: ReturnType<typeof setTimeout> | null = null

  private handleGeminiResponse(data: any) {
    if (data.serverContent?.inputTranscription?.text) {
      this.pendingText += data.serverContent.inputTranscription.text

      // Flush when we have 3+ words
      const words = this.pendingText.trim().split(/\s+/).filter(Boolean)
      if (words.length >= 3) {
        this.flush()
      } else {
        // Flush after 800ms if no more tokens arrive
        if (this.flushTimer) clearTimeout(this.flushTimer)
        this.flushTimer = setTimeout(() => this.flush(), 800)
      }
    }
  }

  private flush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
    const text = this.pendingText.trim()
    if (text) {
      this.onTranscript(text, true)
      this.pendingText = ''
    }
  }

  stop() {
    this.active = false

    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect()
      this.analyserNode = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach(t => t.stop())
      this.stream = null
    }

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close()
      }
      this.ws = null
    }
  }
}
